




















































































































































































































































































































































































































































































































import { Component, Prop, Vue } from 'vue-property-decorator';
import MaterialCard from '@/components/material/Card/index.vue';
import { AppModule } from '@/store/modules/app';
import {
  IGroup,
  IPlanSummaryData,
  PlanUnit,
  BookingTypeEnum,
  TicketSetting,
  Permission,
  TicketPermission,
  BookingPermission,
  AdminPermission,
  TicketPermissionAllowedActionEnum,
  BookingPermissionAllowedActionEnum,
  AdminPermissionAllowedActionEnum,
} from '@/api/types';
import {
  IGetGroupRequest,
  getGroup,
  IPutGroupRequest,
  putGroup,
  listGroups,
} from '@/api/users';
import {
  IListTicketSettingsRequest,
  listTicketSettings,
  IListPlansRequest,
  listPlans,
} from '@/api/tickets';
import { formatApiError, compareString } from '@/utils/index';
import { planToText, ticketToText, TicketType } from '@/utils/tickets';

type ItemType = {
  text: string;
  value: string;
};

type TicketPermissionWithValue = TicketPermission & {
  value: string;
};

type WithPermissions = {
  ticketPermissions: TicketPermission[];
  bookingPermissions: BookingPermission[];
  adminPermissions: AdminPermission[];
};

type WithIsActive = {
  isActive: boolean;
};

@Component({
  name: 'GroupDetail',
  components: {
    MaterialCard,
  },
})
export default class extends Vue {
  @Prop({ required: true }) private id!: string;
  private group = {} as IGroup & WithPermissions & WithIsActive;
  private loading = true;
  private editing = false;
  private valid = false;
  private editingGroup = {} as IGroup & WithPermissions & WithIsActive;
  private ticketSettingsByTicketId: Map<string, TicketSetting> = new Map();
  private ticketItems: ItemType[] = [];
  private ticketTextByTicketId: Map<string, string> = new Map();
  private plansByPlanId: Map<string, IPlanSummaryData> = new Map();
  private planUnitByPlanIdAndUnitId: Map<
    string,
    Map<string, PlanUnit>
  > = new Map();
  private groupByGroupId: Map<string, IGroup> = new Map();
  private groupItems: ItemType[] = [];
  private dialog = false;
  private dialogConfirmHandler = this.resetDialog;
  private dialogTextTitle = '';
  private dialogTextFooter = '';

  get isDialogFullscreen() {
    return false && this.$vuetify.breakpoint.smAndDown;
  }

  get ticketPermissionAllowedActionItems() {
    return Object.values(TicketPermissionAllowedActionEnum)
      .filter(
        p =>
          (p as TicketPermissionAllowedActionEnum) !==
          TicketPermissionAllowedActionEnum.UNKNOWN
      )
      .map(p => ({
        text: this.$t(`TicketPermissionAllowedActionEnum.${p}`),
        value: p as string,
      }));
  }

  get bookingPermissionAllowedActionItems() {
    return Object.values(BookingPermissionAllowedActionEnum)
      .filter(
        p =>
          (p as BookingPermissionAllowedActionEnum) !==
          BookingPermissionAllowedActionEnum.UNKNOWN
      )
      .map(p => ({
        text: this.$t(`BookingPermissionAllowedActionEnum.${p}`),
        value: p as string,
      }));
  }

  get adminPermissionAllowedActionItems() {
    return Object.values(AdminPermissionAllowedActionEnum)
      .filter(
        p =>
          (p as AdminPermissionAllowedActionEnum) !==
          AdminPermissionAllowedActionEnum.UNKNOWN
      )
      .map(p => ({
        text: this.$t(`AdminPermissionAllowedActionEnum.${p}`),
        value: p as string,
      }));
  }

  get canUpdate() {
    return this.valid;
  }

  created() {
    this.fetchData();
  }

  private async fetchData(noLoading = false) {
    if (!noLoading) {
      this.loading = true;
      AppModule.StartProgress();
    }

    await Promise.all([
      this.fetchThenLoadGroup(),
      this.fetchThenLoadGroups(),
      this.fetchThenLoadPlans(),
      this.fetchThenLoadTicketSetting(),
    ]);
    this.loadTicketItems();

    if (!noLoading) {
      this.loading = false;
      AppModule.StopProgress();
    }
  }

  private async fetchThenLoadGroup() {
    try {
      const res = await getGroup({ groupId: this.id });
      this.group = res.group as IGroup & WithPermissions & WithIsActive;
      const ticketPermissions: TicketPermission[] = [];
      const bookingPermissions: BookingPermission[] = [];
      const adminPermissions: AdminPermission[] = [];
      this.group.permissions.forEach(p => {
        !!p.ticketPermission && ticketPermissions.push(p.ticketPermission);
        !!p.bookingPermission && bookingPermissions.push(p.bookingPermission);
        !!p.adminPermission && adminPermissions.push(p.adminPermission);
      });
      this.group.ticketPermissions = ticketPermissions;
      this.group.bookingPermissions = bookingPermissions;
      adminPermissions.forEach(p => {
        p.targetGroupId = p.targetGroupId || '';
      });
      this.group.adminPermissions = adminPermissions;
      this.group.isActive = !this.group.isInactive;
    } catch (err) {
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.$t('Fetch {msg}', {
            msg: this.$t('Group'),
          }) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
    }
  }

  private async fetchThenLoadGroups() {
    try {
      const res = await listGroups({});
      this.groupByGroupId = new Map(res.groups.map(g => [g.id!, g]));
      this.groupItems = res.groups.map(g => ({
        text: g.name,
        value: g.id!,
      }));
    } catch (err) {
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.$t('Fetch {msg}', {
            msg: this.$t('Group'),
          }) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
    }
  }

  private async fetchThenLoadPlans() {
    const res = await listPlans({});
    this.plansByPlanId = new Map(res.plans.map(p => [p.planId, p]));

    const planItems: ItemType[] = [];
    this.plansByPlanId.forEach(p => {
      planItems.push({
        text: planToText(this.$i18n, p),
        value: p.planId,
      });

      if (!this.planUnitByPlanIdAndUnitId.has(p.planId)) {
        this.planUnitByPlanIdAndUnitId.set(p.planId, new Map());
      }
      this.planUnitByPlanIdAndUnitId.set(p.planId, new Map());
    });

    this.loadUnits();
  }

  private loadUnits() {
    this.planUnitByPlanIdAndUnitId = new Map();
    this.plansByPlanId.forEach(p => {
      if (!this.planUnitByPlanIdAndUnitId.has(p.planId)) {
        this.planUnitByPlanIdAndUnitId.set(p.planId, new Map());
      }
      const units = this.planUnitByPlanIdAndUnitId.get(p.planId)!;
      p.units.forEach(u => units.set(u.id, u));
    });
  }

  private async fetchThenLoadTicketSetting() {
    try {
      const res = await listTicketSettings({});
      this.ticketSettingsByTicketId = new Map(res.tickets.map(t => [t.id!, t]));
    } catch (err) {
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.$t('Fetch {msg}', {
            msg: this.$t('Ticket setting'),
          }) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
    }
  }

  private loadTicketItems() {
    const ticketItems: ItemType[] = [
      {
        text: this.$t('All') as string,
        value: 'all',
      },
    ];
    this.ticketSettingsByTicketId.forEach(t => {
      const plan = this.plansByPlanId.get(t.planId)! || {
        activityId: '',
        activityTitle: '-',
        planId: t.planId,
        planTitle: '-',
        bookingType: BookingTypeEnum.EACH_TIME,
      };
      const unit =
        this.planUnitByPlanIdAndUnitId.has(t.planId) &&
        this.planUnitByPlanIdAndUnitId.get(t.planId)!.has(t.unitId)
          ? this.planUnitByPlanIdAndUnitId.get(t.planId)!.get(t.unitId)!
          : {
              id: t.unitId,
              title: '-',
            };
      const ticket: TicketType = {
        activityId: plan.activityId,
        activityTitle: plan.activityTitle,
        planId: plan.planId,
        planTitle: plan.planTitle,
        unitId: unit.id,
        unitTitle: unit.title,
        bookingType: plan.bookingType || BookingTypeEnum.EACH_TIME,
        ticketId: t.id!,
        ticketTitle: t.title,
      };
      ticketItems.push({
        text: ticketToText(this.$i18n, ticket),
        value: t.id!,
      });
    });
    this.ticketItems = ticketItems.sort((a, b) =>
      compareString(a.text, b.text)
    );
    this.ticketTextByTicketId = new Map(
      ticketItems.map(t => [t.value, t.text])
    );
  }

  private resetDialog() {
    this.dialog = false;
    this.dialogTextTitle = '';
    this.dialogTextFooter = '';
    this.$nextTick(() => {
      this.dialogConfirmHandler = this.resetDialog;
    });
  }

  private showDialogForUpdate() {
    this.dialogTextTitle = `${this.$t('Update {msg}', {
      msg: this.$t('Group'),
    })}`;
    this.dialogTextFooter = `${this.$t('Is it OK?')}`;
    this.dialogConfirmHandler = () => {
      this.resetDialog();
      this.updateGroup();
    };
    this.dialog = true;
  }

  private showDialogForResetEditing() {
    this.dialogTextTitle = `${this.$t('Reset')}`;
    this.dialogTextFooter = `${this.$t('Is it OK?')}`;
    this.dialogConfirmHandler = () => {
      this.resetDialog();
      this.startEditingGroup();
    };
    this.dialog = true;
  }

  private showDialogForAbort() {
    this.dialogTextTitle = `${this.$t('Cancel {msg}', {
      msg: this.$t('Update {msg}', {
        msg: this.$t('Group'),
      }),
    })}`;
    this.dialogTextFooter = `${this.$t('Is it OK?')}`;
    this.dialogConfirmHandler = () => {
      this.resetDialog();
      this.abortEditingGroup();
    };
    this.dialog = true;
  }

  private startEditingGroup() {
    this.editing = true;
    this.editingGroup = JSON.parse(JSON.stringify(this.group));
    // fix all type
    this.editingGroup.ticketPermissions.forEach(ticketPermission => {
      const ticket = ticketPermission as TicketPermissionWithValue;
      if (ticket?.ticketId) {
        ticket.value = ticket.ticketId;
      } else if (ticket.all) {
        ticket.value = 'all';
      }
    });
  }

  private abortEditingGroup() {
    this.editing = false;
    this.editingGroup = {} as IGroup & WithPermissions & WithIsActive;
  }

  private updateTicketPermission() {
    const newGroup = JSON.parse(JSON.stringify(this.editingGroup)) as IGroup &
      WithPermissions;
    newGroup.ticketPermissions = newGroup.ticketPermissions.map(
      ticketPermission => {
        const ticket = ticketPermission as TicketPermissionWithValue;
        if (ticket.value === 'all') {
          return {
            action: ticket.action,
            all: true,
          };
        } else {
          return {
            action: ticket.action,
            ticketId: ticket.value,
          };
        }
      }
    );
    return newGroup;
  }

  private async updateGroup() {
    this.loading = true;
    AppModule.StartProgress();

    try {
      const updateGroup = this.updateTicketPermission();
      const permissions: Permission[] = [];
      updateGroup.ticketPermissions.forEach(p =>
        permissions.push({
          ticketPermission: p,
        })
      );
      updateGroup.bookingPermissions.forEach(p =>
        permissions.push({
          bookingPermission: p,
        })
      );
      updateGroup.adminPermissions.forEach(p => {
        if (
          (p.action as AdminPermissionAllowedActionEnum) !==
          AdminPermissionAllowedActionEnum.GROUP_ADMIN
        ) {
          permissions.push({
            adminPermission: {
              action: p.action,
            },
          });
        } else {
          permissions.push({
            adminPermission: {
              action: p.action,
              targetGroupId: p.targetGroupId,
            },
          });
        }
      });
      updateGroup.permissions = permissions;

      const req: IPutGroupRequest = {
        id: updateGroup.id,
        supplierId: updateGroup.supplierId,
        name: updateGroup.name,
        permissions: updateGroup.permissions,
        isInactive: updateGroup.isInactive,
        version: updateGroup.version,
      };

      await putGroup(req);
    } catch (err) {
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.$t('Update {msg}', {
            msg: this.$t('Group'),
          }) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
    }

    this.abortEditingGroup();
    await this.fetchData(true);
    AppModule.StopProgress();
    this.loading = false;
  }

  private addEditingTicketPermission() {
    this.editingGroup.ticketPermissions.push({
      ticketId: '',
      action: TicketPermissionAllowedActionEnum.UNKNOWN,
    });
  }

  private removeEditingTicketPermission(ticketPermissionIndex: number) {
    this.editingGroup.ticketPermissions.splice(ticketPermissionIndex, 1);
  }

  private addEditingBookingPermission() {
    this.editingGroup.bookingPermissions.push({
      action: BookingPermissionAllowedActionEnum.UNKNOWN,
    });
  }

  private removeEditingBookingPermission(bookingPermissionIndex: number) {
    this.editingGroup.bookingPermissions.splice(bookingPermissionIndex, 1);
  }

  private addEditingAdminPermission() {
    this.editingGroup.adminPermissions.push({
      action: AdminPermissionAllowedActionEnum.UNKNOWN,
      targetGroupId: '',
    });
  }

  private removeEditingAdminPermission(adminPermissionIndex: number) {
    this.editingGroup.adminPermissions.splice(adminPermissionIndex, 1);
  }

  private isGroupAdminPermission(adminPermission: AdminPermission) {
    return (
      (adminPermission.action as AdminPermissionAllowedActionEnum) ===
      AdminPermissionAllowedActionEnum.GROUP_ADMIN
    );
  }

  private getGroupName(groupId?: string) {
    return groupId && this.groupByGroupId.has(groupId)
      ? this.groupByGroupId.get(groupId)!.name
      : '';
  }

  private getRequiredRules(
    msg: any,
    exception: (v: any) => boolean = () => false
  ) {
    return [
      (v: string) =>
        exception(v) ||
        !!v ||
        (this.$t('{msg} is required', {
          msg: this.$te(msg) ? this.$t(msg) : msg,
        }) as string),
    ];
  }
}
